import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './StandardsComparison.module.css';

// ─── Formatters ───────────────────────────────────────────────────────────────

function fmtVal(n) {
  if (n === null || n === undefined || !isFinite(n)) return 'N/A';
  return n.toFixed(2) + '%';
}

function fmtMoney(n) {
  if (!n && n !== 0) return 'N/A';
  const abs = Math.abs(n);
  if (abs >= 1e12) return '$' + (n / 1e12).toFixed(1) + 'T';
  if (abs >= 1e9)  return '$' + (n / 1e9).toFixed(1) + 'B';
  if (abs >= 1e6)  return '$' + (n / 1e6).toFixed(1) + 'M';
  return '$' + n.toLocaleString();
}

// ─── Divergence analysis ──────────────────────────────────────────────────────

function buildDivergenceInsights(standards) {
  const { aaoifi, djim, msci } = standards;
  const all = [aaoifi, djim, msci];

  if (aaoifi.verdict === djim.verdict && djim.verdict === msci.verdict) {
    return { agree: true, verdict: aaoifi.verdict, groups: null, reasonKey: null, reasonParams: null };
  }

  // Group standards by verdict
  const groupMap = {};
  for (const s of all) {
    if (!groupMap[s.verdict]) groupMap[s.verdict] = [];
    groupMap[s.verdict].push(s.shortName);
  }

  // Identify the primary reason — most common case is the debt denominator difference
  let reasonKey = null;
  let reasonParams = null;
  const aaoifiDebt = aaoifi.ratios.find(r => r.name === 'Interest-Bearing Debt');
  const djimDebt   = djim.ratios.find(r => r.name === 'Interest-Bearing Debt');

  if (
    aaoifiDebt && djimDebt &&
    aaoifiDebt.value !== null && djimDebt.value !== null &&
    aaoifiDebt.status !== djimDebt.status
  ) {
    const rel = djimDebt.denominator > aaoifiDebt.denominator ? 'larger' : 'smaller';
    reasonKey = 'standards_comparison.divergence_reason.debt_denominator';
    reasonParams = {
      djim_denom:   fmtMoney(djimDebt.denominator),
      aaoifi_denom: fmtMoney(aaoifiDebt.denominator),
      djim_val:     fmtVal(djimDebt.value),
      aaoifi_val:   fmtVal(aaoifiDebt.value),
      rel,
    };
  }

  // Fallback: find first ratio where AAOIFI and DJIM statuses differ
  if (!reasonKey) {
    for (const ratio of aaoifi.ratios) {
      const djimRatio = djim.ratios.find(r => r.name === ratio.name);
      if (djimRatio && ratio.status !== djimRatio.status && ratio.value !== null && djimRatio.value !== null) {
        reasonKey = 'standards_comparison.divergence_reason.ratio_diverge';
        reasonParams = {
          name:          ratio.name,
          aaoifi_val:    fmtVal(ratio.value),
          aaoifi_status: ratio.status,
          djim_val:      fmtVal(djimRatio.value),
          djim_status:   djimRatio.status,
        };
        break;
      }
    }
  }

  if (!reasonKey) {
    reasonKey = 'standards_comparison.divergence_reason.generic';
    reasonParams = {};
  }

  return { agree: false, verdict: null, groups: groupMap, reasonKey, reasonParams };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const STATUS_VALUE_CLASS = { PASS: styles.valPass, WARN: styles.valWarn, FAIL: styles.valFail };
const CHIP_CLASS         = { PASS: styles.chipPass, WARN: styles.chipWarn, FAIL: styles.chipFail };
const BAR_CLASS          = { PASS: styles.barPass,  WARN: styles.barWarn,  FAIL: styles.barFail  };
const VERDICT_CLASS      = {
  COMPLIANT:          styles.verdictCompliant,
  CONDITIONAL:        styles.verdictConditional,
  NON_COMPLIANT:      styles.verdictFail,
  INSUFFICIENT_DATA:  styles.verdictInsufficient,
};

function RatioRow({ ratio }) {
  // Qualitative ratio (Business Activity): show name + tooltip + status chip only
  if (ratio.formula === null) {
    const spoofedWidth = ratio.status === 'PASS' ? 100 : ratio.status === 'WARN' ? 50 : 100;
    return (
      <div className={styles.ratioRow}>
        <div className={styles.ratioNameRow}>
          <span className={styles.ratioName}>{ratio.name}</span>
          {ratio.threshold && (
            <span className={styles.infoIcon} title={ratio.threshold} aria-label={ratio.threshold}>
              ⓘ
            </span>
          )}
        </div>
        <div className={styles.ratioValueRow}>
          <span className={`${styles.ratioValue} ${STATUS_VALUE_CLASS[ratio.status] || ''}`} style={{ flex: 1 }}>
            {ratio.displayNote || '—'}
          </span>
          <span className={`${styles.statusChip} ${CHIP_CLASS[ratio.status] || ''}`}>
            {ratio.status}
          </span>
        </div>
        <div className={styles.barTrack}>
          <div
            className={`${styles.barFill} ${BAR_CLASS[ratio.status] || ''}`}
            style={{ width: spoofedWidth + '%' }}
          />
        </div>
      </div>
    );
  }

  const barWidth = ratio.value !== null && ratio.thresholdValue
    ? Math.min((ratio.value / ratio.thresholdValue) * 100, 110)
    : 0;

  return (
    <div className={styles.ratioRow}>
      <div className={styles.ratioNameRow}>
        <span className={styles.ratioName}>{ratio.name}</span>
        {ratio.isApproximation && (
          <span className={styles.infoIcon} title={ratio.approximationNote} aria-label="approximation note">
            ⓘ
          </span>
        )}
      </div>
      <span className={styles.ratioFormula}>{ratio.formula}</span>
      <div className={styles.ratioValueRow}>
        <span className={`${styles.ratioValue} ${STATUS_VALUE_CLASS[ratio.status] || ''}`}>
          {fmtVal(ratio.value)}
        </span>
        <span className={styles.ratioThreshold}>{ratio.threshold}</span>
        <span className={`${styles.statusChip} ${CHIP_CLASS[ratio.status] || ''}`}>
          {ratio.status}
        </span>
      </div>
      {ratio.value !== null && (
        <div className={styles.barTrack}>
          <div
            className={`${styles.barFill} ${BAR_CLASS[ratio.status] || ''}`}
            style={{ width: barWidth + '%' }}
          />
        </div>
      )}
    </div>
  );
}

function StandardColumn({ std }) {
  const { t } = useTranslation();
  const [basisOpen, setBasisOpen] = useState(false);
  return (
    <div className={styles.col}>
      <div className={styles.colHeader}>
        <div className={styles.colTitleRow}>
          <div>
            <span className={styles.shortName}>{std.shortName}</span>
            <p className={styles.colName}>{std.name}</p>
          </div>
          {std.sourceUrl && (
            <a href={std.sourceUrl} target="_blank" rel="noopener noreferrer" className={styles.sourceLink}>
              {t('standards_comparison.view_methodology')}
            </a>
          )}
        </div>
        <button className={styles.rulingBasisToggle} onClick={() => setBasisOpen(o => !o)}>
          {t('standards_comparison.ruling_basis_toggle')}
          <span className={`${styles.chevron} ${basisOpen ? styles.chevronOpen : ''}`}>▼</span>
        </button>
        {basisOpen && (
          <div className={styles.rulingBasisWrap}>
            {(std.rulingBasisLines || []).map((line, i) => (
              <p key={i} className={styles.rulingBasisLine}>{line}</p>
            ))}
          </div>
        )}
        {/* <p className={styles.denominatorNote}>{std.denominatorNote}</p> */}
        <div className={`${styles.verdictBadge} ${VERDICT_CLASS[std.verdict] || ''}`}>
          {t(`standards_comparison.verdict.${std.verdict}`) || std.verdict.replace(/_/g, ' ')}
        </div>
      </div>
      <div className={styles.ratioList}>
        {std.ratios.map(ratio => (
          <RatioRow key={ratio.name} ratio={ratio} />
        ))}
      </div>
    </div>
  );
}

const AGREE_CLASS = {
  COMPLIANT:         styles.agreeCompliant,
  CONDITIONAL:       styles.agreeConditional,
  NON_COMPLIANT:     styles.agreeFail,
  INSUFFICIENT_DATA: styles.agreeInsufficient,
};

function DivergenceCallout({ insights, onOpenModal }) {
  const { t } = useTranslation();
  return (
    <div className={styles.callout}>
      <div className={styles.calloutHeaderRow}>
        <p className={styles.calloutLabel}>{t('standards_comparison.callout_label')}</p>
        <button className={styles.seeMoreBtn} onClick={onOpenModal}>
          {t('standards_comparison.see_full')}
        </button>
      </div>
      {insights.agree ? (
        <div className={`${styles.agreeBar} ${AGREE_CLASS[insights.verdict] || ''}`}>
          {t('standards_comparison.all_agree', { verdict: insights.verdict.replace('_', ' ') })}
        </div>
      ) : (
        <>
          <div className={styles.groupList}>
            {Object.entries(insights.groups).map(([v, names]) => (
              <div key={v} className={styles.groupRow}>
                <span className={`${styles.groupBadge} ${VERDICT_CLASS[v] || ''}`}>
                  {v.replace('_', ' ')}
                </span>
                <span className={styles.groupNames}>{names.join(', ')}</span>
              </div>
            ))}
          </div>
          {insights.reasonKey && (
            <p className={styles.divergeReason}>{t(insights.reasonKey, insights.reasonParams)}</p>
          )}
        </>
      )}
    </div>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────

function ComparisonModal({ standards, onClose }) {
  const { t } = useTranslation();
  const { aaoifi, djim, msci } = standards;
  const cols = [aaoifi, djim, msci];

  // Collect all unique ratio names across all 3 standards (preserve order)
  const allNames = [];
  const seen = new Set();
  for (const std of cols) {
    for (const ratio of std.ratios) {
      if (!seen.has(ratio.name)) {
        seen.add(ratio.name);
        allNames.push(ratio.name);
      }
    }
  }

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <span className={styles.modalTitle}>{t('standards_comparison.modal_title')}</span>
          <button className={styles.modalClose} onClick={onClose} aria-label={t('standards_comparison.modal_close_aria')}>✕</button>
        </div>
        <div className={styles.modalBody}>
          <table className={styles.modalTable}>
            <thead>
              <tr>
                <th>{t('standards_comparison.table.ratio')}</th>
                <th>AAOIFI</th>
                <th>DJIM</th>
                <th>MSCI</th>
              </tr>
            </thead>
            <tbody>
              {allNames.map(name => (
                <tr key={name}>
                  <td><span className={styles.modalRatioName}>{name}</span></td>
                  {cols.map(std => {
                    const ratio = std.ratios.find(r => r.name === name);
                    if (!ratio) {
                      return <td key={std.id}><span className={styles.modalNa}>—</span></td>;
                    }
                    const valClass = ratio.status === 'PASS' ? styles.valPass
                                   : ratio.status === 'FAIL' ? styles.valFail
                                   : styles.valWarn;
                    const chipClass = ratio.status === 'PASS' ? styles.chipPass
                                    : ratio.status === 'FAIL' ? styles.chipFail
                                    : styles.chipWarn;
                    return (
                      <td key={std.id}>
                        <div className={styles.modalCell}>
                          {ratio.formula === null ? (
                            <span className={`${styles.statusChip} ${chipClass}`}>{ratio.status}</span>
                          ) : (
                            <>
                              <span className={`${styles.modalVal} ${valClass}`}>{fmtVal(ratio.value)}</span>
                              <span className={`${styles.statusChip} ${chipClass}`}>{ratio.status}</span>
                              <span className={styles.modalFormula}>{ratio.formula}</span>
                              <span className={styles.modalNums}>
                                {fmtMoney(ratio.numerator)} ÷ {fmtMoney(ratio.denominator)}
                              </span>
                              <span className={styles.modalThresh}>{t('standards_comparison.table.threshold', { value: ratio.threshold })}</span>
                            </>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export default function StandardsComparison({ standards }) {
  const [modalOpen, setModalOpen] = useState(false);

  if (!standards) return null;

  const insights = buildDivergenceInsights(standards);

  return (
    <div className={styles.wrap}>
      <div className={styles.grid}>
        <StandardColumn std={standards.aaoifi} />
        <StandardColumn std={standards.djim} />
        <StandardColumn std={standards.msci} />
      </div>
      <DivergenceCallout insights={insights} onOpenModal={() => setModalOpen(true)} />
      {modalOpen && <ComparisonModal standards={standards} onClose={() => setModalOpen(false)} />}
    </div>
  );
}
